/**
 * Selling by weight at the till without an electronic scale (2026-10-14: a
 * chicken shop that weighs on a mechanical scale). The cashier types the
 * weight — or the amount the customer asked for („₾10 worth of wings") — in
 * the till's „რამდენი?" window. Stock is kept in the product's own unit
 * (kg, l, m) with three decimals, so a kilogram already holds grams.
 */

import { bestPromoPrice, bundlesFor, type Promotion } from './promotions'
import { specialPrice, type PriceMode } from './cart-pricing'

/** A shop's own codes start at 13 digits; nothing anyone weighs reaches this. */
const MAX_QTY = 100000

const cents = (v: number) => Math.round(v * 100) / 100

/** Three decimals: a gram of a kilogram, a millilitre of a litre. */
export const roundWeight = (v: number) => Math.round(v * 1000) / 1000

export const gramsOf = (kg: number) => Math.round(kg * 1000)

/** Sold by kg, l or m: the line's number is a measure, not a count. */
export const isMeasure = (unit: string | null | undefined): boolean => !!unit && unit !== 'piece'

/**
 * 1.72 — a sale line keeps no unit of its own: its product says. A fraction, sold or
 * still left to return, says so whatever the product says now (deleted, or its unit
 * changed since).
 */
export const isMeasureLine = (unit: string | null | undefined, qty: number, left: number = qty): boolean =>
  isMeasure(unit) || !Number.isInteger(qty) || !Number.isInteger(left)

/**
 * 1.72 — what is left to return of a line, to the gram. A plain subtraction read
 * „0.10000000000000009" in the return window of a 1.1 kg line.
 */
export const returnRoom = (sold: number, returned: number): number => roundWeight(Math.max(0, sold - returned))

/**
 * The line's money in tetri, the way the CART computes it: list × weight less
 * the promotion's discount, which applyPromotions rounds to a tetri on its own
 * — `(list − price) × weight`. With no promotion (`list` = `price`) it is
 * simply price × weight.
 */
export const lineAmount = (weight: number, price: number, list: number = price) =>
  list === price ? cents(weight * price) : cents(list * weight - cents((list - price) * weight))

/**
 * One positive number as a person types it: „0.8", „0,8" (a Georgian keyboard's
 * decimal comma), „.5", „5.". Anything else — and a barcode gun's burst of
 * digits typed into the field — is null.
 */
export function parseQtyInput(raw: string): number | null {
  const s = raw.trim()
  if (!/^\d*(?:[.,]\d*)?$/.test(s) || !/\d/.test(s)) return null
  const v = Number(s.replace(',', '.'))
  if (!Number.isFinite(v) || v <= 0 || v >= MAX_QTY) return null
  return v
}

/**
 * The weight for an amount of money, in grams, chosen so the line comes to
 * that amount when a gram can make it so; otherwise the nearest. Null without
 * a price, or when it is less than a gram.
 */
export function weightForAmount(amount: number, price: number, list: number = price): number | null {
  if (!(price > 0) || !(amount > 0)) return null
  const w = roundWeight(amount / price)
  const want = cents(amount)
  const at = (x: number) => lineAmount(x, price, list)
  const tries = [w, roundWeight(w + 0.001), roundWeight(w - 0.001)].filter((x) => x >= 0)
  const exact = tries.find((x) => at(x) === want)
  const best = exact ?? tries.reduce((a, b) => (Math.abs(at(b) - want) < Math.abs(at(a) - want) ? b : a))
  return best >= 0.001 ? best : null
}

/**
 * The price per unit the window works with — the one the CART will charge for
 * this line (Codex's 1.40 review: „₾10 worth" at the list price under a ₾10/kg
 * promotion handed out 0.833 kg for ₾8.33). The cart's rule (lib/cart-pricing.ts,
 * 1.58): a special price — the customer's group, else the wholesale switch —
 * when it is lower than what the best live price promotion gives, else that
 * promotion, else the list price. In a live BUNDLE the line's money depends on
 * the rest of the cart, so the window cannot know it: `exact: false` — it then
 * asks for a weight only. `list` is what a price promotion discounts from (see
 * `lineAmount`).
 */
export function weighedLinePrice(
  item: { product_id: string; unit_price: number; wholesale_price?: number | null; club_price?: number | null },
  promotions: Promotion[],
  mode: PriceMode,
  now: Date = new Date(),
): { price: number; list: number; exact: boolean } {
  const best = bestPromoPrice(promotions, item.product_id, item.unit_price, now)
  const price = best?.price ?? item.unit_price
  const sp = specialPrice(item, mode)
  if (sp && sp.price < price - 0.005) return { price: sp.price, list: sp.price, exact: true }   // a special line: no promotion
  return { price, list: item.unit_price, exact: bundlesFor(promotions, item.product_id, now).length === 0 }
}


/**
 * 1.63 (Codex final #6) — the line the weight window prices carries its product's club price, so „₾10 worth" for a
 * member weighs at the club price the cart will charge, never at retail (₾10 of goods handed over for ₾8).
 */
export function weighItem<T extends { product_id: string }>(item: T, products: readonly { id: string; club_price?: number | null }[]): T & { club_price: number | null } {
  return { ...item, club_price: products.find((p) => p.id === item.product_id)?.club_price ?? null }
}
